import React, { useMemo } from 'react'
import { X, Download, Route, Mountain, MapPin, FileText, HardDrive } from 'lucide-react'
import { RouteData } from '@cya-routes/shared'

interface GPXDownloadModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  route: RouteData | null
  routeType: 'full' | 'segment'
  parentRouteName?: string
}

export function GPXDownloadModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  route,
  routeType,
  parentRouteName
}: GPXDownloadModalProps) {
  if (!isOpen || !route) return null

  const handleConfirm = () => {
    onConfirm()
    onClose()
  }

  // Estimate file size based on number of coordinates
  const estimatedFileSize = useMemo(() => {
    if (!route.coordinates) return '0 KB'
    
    // Each coordinate with elevation takes roughly 80 bytes in GPX format
    const bytesPerPoint = 80
    const totalBytes = route.coordinates.length * bytesPerPoint + 500 // 500 bytes for GPX headers
    
    if (totalBytes < 1024) {
      return `${totalBytes} bytes`
    } else if (totalBytes < 1024 * 1024) {
      return `${(totalBytes / 1024).toFixed(1)} KB`
    } else {
      return `${(totalBytes / (1024 * 1024)).toFixed(2)} MB`
    }
  }, [route.coordinates])

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      {/* Modal container - positioned at top */}
      <div className="relative min-h-screen flex items-start justify-center p-2 sm:p-4 pt-8 sm:pt-16">
        {/* Modal - positioned at top with responsive scaling */}
        <div className="relative bg-gray-800 rounded-xl shadow-2xl border border-white/10 max-w-md w-full transform scale-75 xs:scale-85 sm:scale-100">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-emerald-600/20 rounded-lg">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-white">Download GPX File</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
          <div className="bg-gray-700/50 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3">
            <div>
              <p className="text-sm text-gray-400 mb-1">
                {routeType === 'segment' ? 'Downloading segment from:' : 'Downloading route:'}
              </p>
              <p className="text-white font-medium">
                {routeType === 'segment' && parentRouteName ? (
                  <>
                    <span className="text-emerald-400">{route.name}</span>
                    <span className="text-gray-400"> from </span>
                    <span className="text-white">{parentRouteName}</span>
                  </>
                ) : (
                  route.name
                )}
              </p>
            </div>

            {/* Route Stats */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 pt-2 sm:pt-3 border-t border-white/10">
              <div className="flex items-center gap-1 sm:gap-2">
                <Route className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-400" />
                <div>
                  <p className="text-[10px] sm:text-xs text-gray-400">Distance</p>
                  <p className="text-xs sm:text-sm font-medium text-white">
                    {Math.round(route.stats?.distance || 0)} km
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <Mountain className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-400" />
                <div>
                  <p className="text-[10px] sm:text-xs text-gray-400">Elevation</p>
                  <p className="text-xs sm:text-sm font-medium text-white">
                    {(route.elevation?.up || 0).toLocaleString()} m
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <HardDrive className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-400" />
                <div>
                  <p className="text-[10px] sm:text-xs text-gray-400">File Size</p>
                  <p className="text-xs sm:text-sm font-medium text-white">
                    {estimatedFileSize}
                  </p>
                </div>
              </div>
            </div>

            {/* Location */}
            {route.location && (
              <div className="flex items-center gap-2 pt-3 border-t border-white/10">
                <MapPin className="w-4 h-4 text-emerald-400" />
                <p className="text-sm text-gray-300">
                  {typeof route.location === 'string' 
                    ? route.location 
                    : `${route.location.region || ''}, ${route.location.state || ''}, ${route.location.country || ''}`.replace(/^, |, $|, ,/g, '').trim()
                  }
                </p>
              </div>
            )}
          </div>

          {/* GPX Info */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-white">This GPX file includes:</p>
            <ul className="space-y-1 text-sm text-gray-300">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                GPS coordinates for the entire {routeType === 'segment' ? 'segment' : 'route'}
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                Elevation data for each waypoint
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                Compatible with all major GPS devices and apps
              </li>
            </ul>
          </div>

          <div className="bg-blue-600/10 border border-blue-600/20 rounded-lg p-3">
            <p className="text-xs text-blue-400">
              The file will be saved as: <span className="font-mono">{route.name.replace(/[^a-zA-Z0-9]/g, '_')}.gpx</span>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-white/10 flex gap-2 sm:gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-3 sm:px-4 py-2 border border-white/10 rounded-lg text-sm sm:text-base text-gray-300 hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-3 sm:px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-white font-medium text-sm sm:text-base transition-colors flex items-center justify-center gap-1 sm:gap-2"
          >
            <Download className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden xs:inline">Download</span> GPX
          </button>
        </div>
        </div>
      </div>
    </div>
  )
}